import { ApolloClient, type DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_PRODUCT_CATEGORY,
  GET_MASTER_DATA_PRODUCT_CATEGORIES,
  UPDATE_MASTER_DATA_PRODUCT_CATEGORY,
} from "@src/domain/graphql/masterData";
import {
  MasterDataProductCategory,
  type IMasterDataProductCategory,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataProductCategoryDTO,
  GetMasterDataProductCategoriesDTO,
  UpdateMasterDataProductCategoryDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataProductCategoryRepository,
  MasterDataProductCategoriesListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
  toMasterDataListResult,
} from "./masterDataRepository.shared";

function mapCategoryItem(
  item: IMasterDataProductCategory,
): MasterDataProductCategory {
  return MasterDataProductCategory.from({
    productCategoryId: item.productCategoryId,
    categoryKey: item.categoryKey,
    name: item.name,
    description: item.description ?? null,
    isActive: item.isActive,
    searchVectorKeywords: item.searchVectorKeywords ?? null,
  });
}

function toCategoryMutationParams(
  params: CreateMasterDataProductCategoryDTO | UpdateMasterDataProductCategoryDTO,
) {
  return {
    name: params.name,
    categoryKey: params.categoryKey,
    description: params.description ?? null,
    searchVectorKeywords: params.searchVectorKeywords ?? null,
    isActive: params.isActive,
  };
}

export class MasterDataProductCategoryRepository
  implements IMasterDataProductCategoryRepository
{
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getProductCategories(
    params: GetMasterDataProductCategoriesDTO,
  ): Promise<MasterDataProductCategoriesListResult> {
    const { data, error } = await this.client.query<{
      masterDataProductCategories: {
        items: IMasterDataProductCategory[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_MASTER_DATA_PRODUCT_CATEGORIES,
      variables: {
        params: {
          search: params.search ?? null,
          includeInactive: params.includeInactive ?? true,
          ...(params.page_number != null
            ? { page_number: params.page_number }
            : {}),
          ...(params.page_size != null ? { page_size: params.page_size } : {}),
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataProductCategories;
    if (!response) {
      throw new Error("Failed to fetch master data product categories");
    }

    return toMasterDataListResult(
      response,
      mapCategoryItem,
      params.page_number ?? response.page ?? 1,
      params.page_size ?? response.limit ?? response.total ?? 0,
    );
  }

  async createProductCategory(
    params: CreateMasterDataProductCategoryDTO,
  ): Promise<MasterDataProductCategory> {
    return this.putProductCategory(
      CREATE_MASTER_DATA_PRODUCT_CATEGORY,
      toCategoryMutationParams(params),
      (data) => data?.createMasterDataProductCategory,
      "Failed to create product category",
    );
  }

  async updateProductCategory(
    params: UpdateMasterDataProductCategoryDTO,
  ): Promise<MasterDataProductCategory> {
    return this.putProductCategory(
      UPDATE_MASTER_DATA_PRODUCT_CATEGORY,
      {
        productCategoryId: params.productCategoryId,
        ...toCategoryMutationParams(params),
      },
      (data) => data?.updateMasterDataProductCategory,
      "Failed to update product category",
    );
  }

  private async putProductCategory(
    mutation: DocumentNode,
    mutationParams: Record<string, unknown>,
    pickResponse: (
      data:
        | {
            createMasterDataProductCategory?: IMasterDataProductCategory;
            updateMasterDataProductCategory?: IMasterDataProductCategory;
          }
        | null
        | undefined,
    ) => IMasterDataProductCategory | null | undefined,
    fallbackMessage: string,
  ): Promise<MasterDataProductCategory> {
    const result = await this.client.mutate<{
      createMasterDataProductCategory?: IMasterDataProductCategory;
      updateMasterDataProductCategory?: IMasterDataProductCategory;
    }>({
      mutation,
      variables: { params: mutationParams },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapCategoryItem(
      requireMasterDataMutationData(
        pickResponse(result.data),
        result.error,
        fallbackMessage,
      ),
    );
  }
}
